import { motion, useReducedMotion } from "framer-motion";
import { ArrowDown, ArrowUp, Minus } from "lucide-react";
import { pctLabel, deltaLabel, deltaDirection } from "../lib/format";
import { DUR, EASE_OUT } from "../lib/motion";

const METRICS = [
  { key: "jaccard", label: "Jaccard" },
  { key: "dice", label: "Dice" },
  { key: "cosine", label: "Cosine" },
];

function Bar({ value, series, delay }) {
  const prefersReducedMotion = useReducedMotion();
  const safe = Number.isFinite(value) ? Math.min(Math.max(value, 0), 1) : 0;

  return (
    <div className="bars__track">
      <motion.span
        className={`bars__fill bars__fill--${series}`}
        initial={{ scaleX: prefersReducedMotion ? safe : 0 }}
        whileInView={{ scaleX: safe }}
        viewport={{ once: true, margin: "0px 0px -10% 0px" }}
        transition={
          prefersReducedMotion ? { duration: 0 } : { duration: DUR.reveal, ease: EASE_OUT, delay }
        }
      />
    </div>
  );
}

/**
 * Grouped bars contrasting the raw content stream ("before") with the visible
 * text recovered from the page ("after") across all three metrics.
 *
 * The two series differ by colour *and* by hatching *and* by an explicit row
 * label, so the comparison survives colour-blindness and greyscale printing.
 * A visually-hidden table repeats the same figures for screen readers, since a
 * chart on its own is not readable assistive content.
 */
function CompareBars({ before, after }) {
  const hasBefore = Boolean(before);

  return (
    <div>
      <div className="bars">
        {METRICS.map((metric, index) => {
          const afterValue = after?.[metric.key];
          const beforeValue = before?.[metric.key];
          const delta = hasBefore ? afterValue - beforeValue : null;

          return (
            <div className="bars__row" key={metric.key}>
              <span className="bars__name">{metric.label}</span>
              <div className="bars__pair">
                {hasBefore && (
                  <div className="bars__line">
                    <Bar value={beforeValue} series="before" delay={index * 0.06} />
                    <span className="bars__num">{pctLabel(beforeValue, 1)}</span>
                  </div>
                )}
                <div className="bars__line">
                  <Bar value={afterValue} series="after" delay={index * 0.06 + 0.08} />
                  <span className="bars__num">{pctLabel(afterValue, 1)}</span>
                </div>
                {hasBefore && (
                  <span className="bars__delta" data-dir={deltaDirection(delta)}>
                    {deltaDirection(delta) === "up" && <ArrowUp size={10} aria-hidden="true" />}
                    {deltaDirection(delta) === "down" && <ArrowDown size={10} aria-hidden="true" />}
                    {deltaDirection(delta) === "flat" && <Minus size={10} aria-hidden="true" />}
                    {deltaDirection(delta) === "flat"
                      ? "unchanged after removal"
                      : `${deltaLabel(delta)} after removal`}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {hasBefore && (
        <div className="legend">
          <span className="legend__item">
            <span className="legend__swatch legend__swatch--before" aria-hidden="true" />
            Before · straight from the file
          </span>
          <span className="legend__item">
            <span className="legend__swatch legend__swatch--after" aria-hidden="true" />
            After · visible text only
          </span>
        </div>
      )}

      <table className="sr-only">
        <caption>Similarity scores by metric and extraction method</caption>
        <thead>
          <tr>
            <th scope="col">Metric</th>
            {hasBefore && <th scope="col">Before, straight from the file</th>}
            <th scope="col">After, injected characters removed</th>
          </tr>
        </thead>
        <tbody>
          {METRICS.map((metric) => (
            <tr key={metric.key}>
              <th scope="row">{metric.label}</th>
              {hasBefore && <td>{pctLabel(before[metric.key])}</td>}
              <td>{pctLabel(after?.[metric.key])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default CompareBars;
